import { ArrowLeft } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Seo } from "@/components/Seo";
import { Card, CardContent } from "@/components/ui/card";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { useAuth } from "@/features/auth/useAuth";
import { canAccessKpiDepartment, canCollectKpis } from "@/features/kpi/access";
import {
  KpiBackendNotice,
  KpiPeriodToolbar,
  KpiTable,
} from "@/features/kpi/components";
import {
  DEPARTMENTS,
  kpisForDepartment,
  type Department,
} from "@/features/kpi/definitions";
import { buildKpiCsv, downloadKpiCsv } from "@/features/kpi/export";
import { useKpiWorkspace } from "@/features/kpi/useKpiWorkspace";

export default function InternalKpiSection() {
  const { department: departmentId } = useParams();
  const { user } = useAuth();
  const department = DEPARTMENTS.find((item) => item.id === departmentId);
  const workspace = useKpiWorkspace();

  if (!department) return <Navigate to="/app/internal/kpis" replace />;
  if (!canAccessKpiDepartment(user, department.id)) {
    return <Navigate to="/app/internal/kpis" replace />;
  }

  const departmentSlug = department.id;
  const definitions = kpisForDepartment(departmentSlug as Department);
  const definitionIds = new Set(definitions.map((def) => def.id));
  const dirtyIds = workspace.dirtyIds.filter((id) => definitionIds.has(id));
  const entered = definitions.filter((def) => workspace.current.has(def.id)).length;
  const canCollect = canCollectKpis(user);

  function exportCsv() {
    downloadKpiCsv(
      `kpi-${departmentSlug}-${workspace.periodType}-${workspace.periodStart}.csv`,
      buildKpiCsv(
        definitions,
        workspace.current,
        workspace.previous,
        workspace.periodType,
        workspace.periodStart,
      ),
    );
  }

  return (
    <>
      <Seo title={`${department.title} KPIs`} noindex />
      <DemoBanner />
      <Link
        to="/app/internal/kpis"
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-brand-500 hover:text-brand-900"
      >
        <ArrowLeft className="h-4 w-4" />
        All KPI sections
      </Link>
      <div>
        <h1 className="text-2xl font-bold">{department.title}</h1>
        <p className="mt-1 max-w-3xl text-sm text-brand-500">{department.blurb}</p>
      </div>
      <KpiBackendNotice backend={workspace.backend} />
      <KpiPeriodToolbar
        periodType={workspace.periodType}
        periodStart={workspace.periodStart}
        onPeriodType={workspace.switchPeriodType}
        onPeriodStart={workspace.setPeriodStart}
        entered={entered}
        total={definitions.length}
        dirtyCount={dirtyIds.length}
        saving={workspace.saving}
        saved={workspace.saved}
        onSave={() => void workspace.save(dirtyIds)}
        collecting={workspace.collecting}
        onCollect={canCollect ? (force) => void workspace.collectAuto(force) : undefined}
        lastCollectAt={workspace.lastCollectAt}
        collectFresh={workspace.collectFresh}
        onExport={exportCsv}
      />
      {workspace.collectNote && (
        <FeedbackMessage tone="info" className="mt-4">{workspace.collectNote}</FeedbackMessage>
      )}
      {workspace.error && (
        <FeedbackMessage tone="error" className="mt-4">{workspace.error}</FeedbackMessage>
      )}
      {workspace.loading ? (
        <div className="mt-6"><CardSkeleton rows={6} /></div>
      ) : (
        <Card className="mt-6">
          <CardContent className="p-5">
            <KpiTable
              definitions={definitions}
              current={workspace.current}
              previous={workspace.previous}
              allEntries={workspace.entries}
              draft={workspace.draft}
              onDraftChange={(id, value) =>
                workspace.setDraft((current) => ({ ...current, [id]: value }))
              }
              dirtyIds={dirtyIds}
              periodType={workspace.periodType}
              periodStart={workspace.periodStart}
              userId={user?.id}
              onLogSynced={() => void workspace.reload()}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
}
